import React, {useState, useEffect, useContext, useCallback, useMemo} from 'react';
import {
    Box,
    CircularProgress,
    Chip,
    Tabs,
    Tab,
    Typography,
    IconButton,
    Grid,
    Paper,
} from '@mui/material';
import SelectedFaxContext from './SelectedFaxContext';
import {GET_RECEIVED_FAX_DETAILS} from '../gql/FaxQueries';
import { useQuery } from '../../../common/apollo-graphql';
import {
    Description as FileTextIcon,
    LocalHospital as StethoscopeIcon,
    AccessTime as ClockIcon,
    CheckCircle,
    ChevronLeft,
} from '@mui/icons-material';
import DetectedReferralCard, {CreateReferralCard} from './DetectedReferralCard';
import {MyTypography} from '../../common/components';
import MyPdfViewer from '../../common/components/MyPdfViewer';
import CreateDetectedReferralDialog from './CreateDetectedReferralDialog';
import {
    PROCESSED_STATUS_RECEIVED_FAX,
    RECEIVED_FAX_PROCESSING_STATUSES,
} from '../constants/fax.consants';

export function useGetReceivedFaxDetails(selectedFaxId) {
    const [details, setDetails] = useState(null);

    const { loading, data, error } = useQuery(GET_RECEIVED_FAX_DETAILS, {
        skip: !selectedFaxId,
        variables: {
            id: selectedFaxId,
        },
    });

    useEffect(() => {
        if (!data || !data.inbound_fax_details) return;

        const initialDetails = {
            ...data.inbound_fax_details,
            'detected_referrals': data?.inbound_fax_details?.processed_data?.detected_referrals ?? [],
            'actions': data?.inbound_fax_details?.actions,
        };

        if (data.inbound_fax_details.pdf_b64) {
            // Decode Base64 string
            const binaryData = atob(data.inbound_fax_details.pdf_b64);

            // Convert binary string to Uint8Array
            const byteArray = Uint8Array.from(binaryData, char => char.charCodeAt(0));

            // Create a Blob and Object URL
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);

            initialDetails['pdf_blob_url'] = blobUrl;
        }

        if (initialDetails.detected_referrals.length > 0) {
            initialDetails.detected_referrals = initialDetails.detected_referrals.map((item) => {
                return {
                    ...item,
                    // form_input_suggestions: getFieldValuesFromReferralObject(item),
                };
            });
        }

        setDetails(initialDetails);

        return () => {
            setDetails(null);
        };
    }, [data]);

    return {
        loading,
        details,
        error,
    };
}

export function ReceivedFaxBlankDetails() {
    return (
        <Box
            sx={{
                height: '100%',
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "grey.50",
            }}
        >
            <Box sx={{ textAlign: "center" }}>
                <FileTextIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                    Select a Fax
                </Typography>
                <Typography color="text.secondary">
                    Choose a fax from the list to view details and process referrals
                </Typography>
            </Box>
        </Box>
    );
}

export default function ReceivedFaxDetails({onCloseFaxDetails}) {
    const selectedFax = useContext(SelectedFaxContext);

    const [selectedFaxReferral, setSelectedFaxReferral] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const faxId = selectedFax
        ? selectedFax.id
        : null;

    const { loading, details, error } = useGetReceivedFaxDetails(faxId);

    const referralsDetectedCount = details?.detected_referrals?.length ?? 0;

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleOnClickCreateReferral = useCallback((referral={}) => () => {
        setOpenDialog(true);
        setSelectedFaxReferral(referral);
    }, []);

    const handleOnCloseDialog = useCallback(() => {
        setOpenDialog(false);
    }, []);

    const detectedReferralsLabel = useMemo(() => {
        if (selectedFax && RECEIVED_FAX_PROCESSING_STATUSES.includes(selectedFax.status)) {
            return 'Processing OCR...';
        }

        if (loading) {
            return 'Loading Referrals...';
        }

        return referralsDetectedCount > 0
               ? `Detected Referrals (${referralsDetectedCount})`
               : 'No Referrals Detected';
    }, [loading, selectedFax, referralsDetectedCount]);

    useEffect(() => {
        if (openDialog === false) {
            setSelectedFaxReferral(null);
        }
    }, [openDialog]);

    if (!selectedFax) {
        return (<ReceivedFaxBlankDetails />);
    }

    return (<>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Details Header */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton onClick={onCloseFaxDetails} sx={{ color: "text.secondary" }}>
                        <ChevronLeft />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold">
                            Fax Details
                        </Typography>
                        <Typography color="text.secondary">
                            From {selectedFax.metadata.from_number} • Received {selectedFax.metadata.date_created}
                        </Typography>
                    </Box>
                    <Chip
                        icon={selectedFax.status === PROCESSED_STATUS_RECEIVED_FAX ? <CheckCircle /> : <ClockIcon />}
                        label={selectedFax.status}
                        color={selectedFax.status === PROCESSED_STATUS_RECEIVED_FAX ? "success" : "default"}
                    />
                </Box>
            </Box>

            <Box sx={{ flex: 1, p: 1, overflowY: "auto" }}>
                <Paper sx={{mb: 1}}>
                    <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                        <Tab
                            icon={loading ? <CircularProgress size={24} /> : <StethoscopeIcon />}
                            label={detectedReferralsLabel}
                            iconPosition="start"
                        />
                        <Tab icon={<FileTextIcon />} label="PDF Content" iconPosition="start" />
                    </Tabs>
                </Paper>

                {
                    activeTab === 0 &&
                    <Grid container spacing={2}>
                        {
                            loading
                            ? null
                            : (
                                referralsDetectedCount > 0
                                ? details?.detected_referrals?.map((referral, index) => (
                                    <Grid item xs={12} md={6} key={`detected-referral-${index}`}>
                                        <DetectedReferralCard
                                            referral={referral}
                                            onClickCreateReferral={handleOnClickCreateReferral(referral)}
                                        />
                                    </Grid>
                                ))
                                : null
                            )
                        }
                        <Grid item
                              xs={12}
                              md={6}
                              key="create-referral-card">
                            <CreateReferralCard
                                onClickCreateReferral={handleOnClickCreateReferral()}
                            />
                        </Grid>
                    </Grid>
                }

                {
                    activeTab === 1 &&
                    (
                    <Box>
                        {/* PDF Viewer Placeholder */}
                        <Paper
                            sx={{
                                textAlign: 'center',
                                minHeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {/* Wrap PDF Preview in a Scrollable Container */}
                            {
                                loading
                                ? <Box>
                                        <FileTextIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            PDF Viewer
                                        </Typography>
                                        <Typography color="text.secondary">Please wait loading pdf now...</Typography>
                                        <CircularProgress />
                                    </Box>
                                : (
                                    details && details.pdf_blob_url
                                    ? <Box
                                          sx={{
                                              flex: 1,
                                              overflowY: 'auto',
                                              maxHeight: '75vh',
                                              minHeight: 500,
                                              display: 'flex', // Add flexbox
                                          }}
                                      >
                                        <MyPdfViewer fileUrl={details.pdf_blob_url} />
                                    </Box>
                                    : <MyTypography>No pdf loaded</MyTypography>
                                )
                            }
                        </Paper>
                    </Box>
                    )
                }
            </Box>
        </Box>

        <CreateDetectedReferralDialog
            open={openDialog}
            onClose={handleOnCloseDialog}
            selectedReferral={{
                receivedFaxId: selectedFax.id,
                defaultValues: selectedFaxReferral?.form_suggestion,
                fromFax: selectedFax?.metadata?.from_number,
                pdf: {
                    fileName: selectedFax ? `fax-${selectedFax.external_id}.pdf` : '',
                    blobUrl: details?.pdf_blob_url,
                    pageCount: selectedFax?.metadata?.pages_count,
                },
                ocrDetails: selectedFaxReferral,
                ocrSuggestedPages: selectedFaxReferral?.pdf_pages,
                actions: details?.actions,
            }}
        />
    </>);
}
